import { useDispatch, useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';

import { DATE_FORMAT } from 'modules/api/constants';
import warningSvg from 'assets/icons/warning.svg';
import { Store } from 'components/common/types/Store.types';

import { changeDealCurrentLineData } from 'store/dealManagement/reducer';
import { DealStatus } from 'components/common/types/Deal.types';
import DayPicker from 'lib/DatePicker/DayPicker';
import { hasLineStarted } from 'components/pages/DealWithLines/common/getActionConditions';
import InfoMessage from 'components/common/Deal/InfoMessage';
import { INFO_TYPE } from 'components/common/Deal/InfoMessage/InfoMessage.types';
import { getDealFormReadOnly } from 'store/dealManagement/selectors/getDealFormReadOnly';
import { getCurrentLine } from 'store/dealManagement/selectors/getCurrentLine';
import { getCurrentLineEndDate, getCurrentLineStartDate } from 'store/dealManagement/selectors/getCurrentLineSchedule';
import { getBackupLines } from 'store/dealManagement/selectors/getBackupLines';
import { getDealType } from 'store/dealManagement/selectors/getDealType';
import { getAsDate } from 'utils/getAsDate';

import { getEndOfToday, getScheduleWarningMessage, getStartOfToday } from './dateSelector.utils';
import { useDateSelectorSchedule } from './useDateSelectorSchedule';

type DateSelectorProps = {
  handlePatternInfoMessage?: () => void;
  isPatternInfoMessage?: boolean;
};

const DateSelector = ({
  handlePatternInfoMessage = () => {},
  isPatternInfoMessage = false,
}: DateSelectorProps): React.ReactElement => {
  const dispatch = useDispatch();
  const lines = useSelector(getBackupLines);
  const bookingStatusCode = useSelector((state: Store) => state.dealManagement.commonDeal.bookingStatusCode);
  const dealType = useSelector(getDealType);
  const currentLine = useSelector(getCurrentLine);
  const startDate = useSelector(getCurrentLineStartDate);
  const endDate = useSelector(getCurrentLineEndDate);

  const readOnly = useSelector(getDealFormReadOnly);
  const isPendingReservation = bookingStatusCode === DealStatus.PENDING_RESERVATION;
  const isDealLive = bookingStatusCode === DealStatus.LIVE;
  const isDealPendingOrRejected = [DealStatus.PENDING_APPROVAL, DealStatus.REJECTED].includes(bookingStatusCode);
  const backupLine = lines.find(({ id }) => id === currentLine.id);

  const { disabledEndDates, changeEndDate } = useDateSelectorSchedule({
    dealType,
    startDate,
    endDate,
    handlePatternInfoMessage,
    isPatternInfoMessage,
  });

  const warningMessage = getScheduleWarningMessage(isDealPendingOrRejected, currentLine);
  const isStartDateDisabled = readOnly || (isDealLive && hasLineStarted(backupLine)) || isPendingReservation;
  const startOfToday = getStartOfToday();
  const startDisabledDays = [{ before: startOfToday }];

  return (
    <div className="flex flex-row space-x-5 flex-shrink-0">
      <div>
        <label htmlFor="schedule-start-date" className="sub-header-base text-neutral-950-opacity-60 mb-2">
          Start date
        </label>
        <DayPicker
          inputId="schedule-start-date"
          format={DATE_FORMAT}
          disabledDays={startDisabledDays}
          onDayChange={(newStartDate) => dispatch(changeDealCurrentLineData({ startDate: newStartDate }))}
          selectedDay={getAsDate(startDate, getStartOfToday())}
          fromMonth={startOfToday}
          isDisabled={isStartDateDisabled}
        />
      </div>
      <div className="pr-4">
        <label htmlFor="schedule-end-date" className="sub-header-base text-neutral-950-opacity-60 mb-2">
          End date
        </label>
        <div className="relative">
          <DayPicker
            inputId="schedule-end-date"
            format={DATE_FORMAT}
            disabledDays={[{ before: disabledEndDates }]}
            onDayChange={changeEndDate}
            selectedDay={getAsDate(endDate, getEndOfToday())}
            fromMonth={disabledEndDates}
            isDisabled={readOnly || isPendingReservation}
          />
          {warningMessage ? (
            <div className="absolute top-2 -right-8">
              <InfoMessage
                type={INFO_TYPE.TOOLTIP}
                icon={<SVG src={warningSvg} className="fill-current text-yellow" />}
              >
                <div className="w-48">{warningMessage}</div>
              </InfoMessage>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DateSelector;
